import * as React from 'react';
import {
  keyframes,
  useDebounce,
  assignProps,
  createViewConfig,
  createGenericView,
} from '@sail/engine';
import {tokens} from '@sail/tokens';
import type {View} from '../../view';
import {view} from '../../view';

export type CodePuncherDigitData = {
  value: string;
  active?: boolean;
  disabled?: boolean;
};

export type CodePuncherDigitProps = View.IntrinsicElement<
  'div',
  {
    hasCaret?: boolean;
    isHighlighted?: boolean;
  } & CodePuncherDigitData
>;

export const CodePuncherDigitConfig = createViewConfig({
  props: {} as CodePuncherDigitProps,
  name: 'CodePuncherDigit',
  defaults: {
    value: '',
  },
});

const caretBlinkKeyframes = keyframes({
  '0%, 70%, 100%': {
    opacity: 1,
  },
  '20%, 50%': {
    opacity: 0,
  },
});

const FakeCaret = createGenericView(() => {
  return (
    <view.div
      css={{
        stack: 'x',
        inset: 'space.0',
        position: 'absolute',
        pointerEvents: 'none',
        alignItems: 'center',
        justifyContent: 'center',
        animation: `${caretBlinkKeyframes} 1.25s ease-out infinite`,
      }}
    >
      <view.div
        css={{
          width: 'xxsmall',
          height: 'xlarge',
          borderRadius: 'xsmall',
          backgroundColor: 'feedback.neutral.inverted',
        }}
      />
    </view.div>
  );
});

export const CodePuncherDigit = CodePuncherDigitConfig.createView(
  ({value, hasCaret, isHighlighted, ...props}) => {
    const [isVisuallyHighlighted] = useDebounce(isHighlighted, 50);

    return (
      <view.div {...props}>
        <view.span
          css={{
            font: 'heading.xlarge.subdued',
            transition: `background-color ${tokens['animation.duration.short']} ${tokens['animation.easing']}`,
            backgroundColor: isVisuallyHighlighted
              ? tokens['color.brand.100']
              : 'transparent',
          }}
        >
          {value}
        </view.span>
        {hasCaret && !value ? <FakeCaret /> : null}
      </view.div>
    );
  },
  {
    css: {
      width: 'space.500',
      height: 'xxlarge',

      position: 'relative',

      stack: 'x',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'form',

      color: 'form',

      cursor: 'pointer',
      userSelect: 'none',

      transition: 'all 0.1s ease-in-out',

      ':first-child': {
        borderTopLeftRadius: 'medium',
        borderBottomLeftRadius: 'medium',
      },

      ':last-child': {
        borderTopRightRadius: 'medium',
        borderBottomRightRadius: 'medium',
      },
    },
    uses: [
      assignProps<CodePuncherDigitProps>((props) => ({
        hasCaret: props.active && !props.value,
        isHighlighted: props.active && !!props.value,
      })),
    ],
    variants: {
      disabled: {
        true: {
          css: {
            pointerEvents: 'none',
            color: 'form.disabled',
            backgroundColor: 'form.disabled',
          },
        },
        false: {},
      },
      active: {
        true: {
          css: {
            zIndex: 1,
            focusRing: 'focus',
          },
        },
        false: {
          css: {
            keyline: 'border',
          },
        },
      },
    },
  },
);
